import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import Edit from "@material-ui/icons/Edit";
import DeleteOutline from "@material-ui/icons/DeleteOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import PlanModal from "../../components/PlanModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
}));

const Plans = () => {
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modal, setModal] = useState({ open: false, plan: null });
  const [toDelete, setToDelete] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await api.get("/plans");
      setPlans(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleDelete = async () => {
    try {
      await api.delete(`/plans/${toDelete.id}`);
      toast.success(i18n.t("plans.toasts.deleted"));
      fetchPlans();
    } catch (err) {
      toastError(err);
    }
    setToDelete(null);
  };

  return (
    <MainContainer>
      <PlanModal
        open={modal.open}
        plan={modal.plan}
        onSaved={fetchPlans}
        onClose={() => setModal({ open: false, plan: null })}
      />
      <ConfirmationModal
        title={toDelete ? i18n.t("plans.confirmDelete", { name: toDelete.name }) : ""}
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
      <MainHeader>
        <Title>{i18n.t("plans.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModal({ open: true, plan: null })}
          >
            {i18n.t("plans.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("plans.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("plans.table.users")}</TableCell>
              <TableCell align="center">{i18n.t("plans.table.connections")}</TableCell>
              <TableCell align="center">{i18n.t("plans.table.queues")}</TableCell>
              <TableCell align="center">{i18n.t("plans.table.campaigns")}</TableCell>
              <TableCell align="center">{i18n.t("plans.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell>{plan.name}</TableCell>
                <TableCell align="center" data-label={i18n.t("plans.table.users")}>
                  {plan.maxUsers}
                </TableCell>
                <TableCell align="center" data-label={i18n.t("plans.table.connections")}>
                  {plan.maxConnections}
                </TableCell>
                <TableCell align="center" data-label={i18n.t("plans.table.queues")}>
                  {plan.maxQueues}
                </TableCell>
                <TableCell align="center" data-label={i18n.t("plans.table.campaigns")}>
                  {i18n.t(plan.useCampaigns ? "plans.yes" : "plans.no")}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => setModal({ open: true, plan })}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => setToDelete(plan)}>
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ResponsiveTable>
      </Paper>
    </MainContainer>
  );
};

export default Plans;
